import {
  ANY_AIB_REGISTRY_SENTINEL,
  SUPPORTED_REGISTRIES_WITH_LABELS,
} from "@/constants";
import {
  COUNTRY_NAMES,
  translateCountryCode,
  type TranslationExistsFn,
  type TranslationFn,
} from "@/utils/countries";

export type { TranslationFn } from "@/utils/countries";

/**
 * Get a translated registry label combining country name and operator.
 * @param registryCode - The registry code (e.g., "EE", "BE-BRU"), or null/undefined
 * @param t - The i18n translation function
 * @param te - Optional i18n key-existence check; when omitted, falls back to string equality
 * @returns Translated label like "Estonia (Elering)", "-" for null/undefined, or the code if not found
 */
export const getRegistryLabel = (
  registryCode: string | null | undefined,
  t: TranslationFn,
  te?: TranslationExistsFn
): string => {
  if (!registryCode) return "-";
  const registry = SUPPORTED_REGISTRIES_WITH_LABELS.find(
    (r) => r.value === registryCode
  );
  if (!registry) return registryCode;
  const countryName = translateCountryCode(registryCode, t, te);
  return `${countryName} (${registry.operator})`;
};

const REGISTRY_VALUES = new Set(
  SUPPORTED_REGISTRIES_WITH_LABELS.map((registry) => registry.value)
);

const COUNTRY_NAME_TO_CODE = new Map(
  Object.entries(COUNTRY_NAMES).map(([code, name]) => [
    name.toLowerCase(),
    code,
  ])
);

const BELGIUM_REGION_TO_REGISTRY: Record<string, string> = {
  federal: "BE-FED",
  flanders: "BE-FLA",
  wallonia: "BE-WAL",
  brussels: "BE-BRU",
};

// AIB-hub aliases (in the collapsed lookup form: lowercased, "[-_\s]+" → single space) that resolve
// to Norway's registry (Statnett). Mirrors the backend AIB_HUB_ALIASES in
// app/utils/pydantic_validators.py — keep the two in sync.
const AIB_HUB_ALIASES = new Set(["eu aib", "aib", "aib hub"]);

/**
 * Normalize AI extracted country/region to a valid registry selection value.
 * Returns null when the value cannot be mapped to a supported registry.
 */
export const normalizeRegistryCountryRegion = (
  rawValue: string | null | undefined
): string | null => {
  if (!rawValue) {
    return null;
  }

  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }

  // DEV-576: preserve the "Any AIB" sentinel verbatim. The backend AI import emits it (in place of
  // the AIB→NO alias) so it lands on the editor's "Any AIB" option; this re-normalization must not
  // drop it. It is never submitted as a region — the editor materializes it to "NO" on submit.
  if (trimmed.toUpperCase() === ANY_AIB_REGISTRY_SENTINEL) {
    return ANY_AIB_REGISTRY_SENTINEL;
  }

  // "EU AIB" refers to Norway's registry (Statnett), used for generic AIB-wide cancellations
  const normalizedAlias = trimmed
    .toLowerCase()
    .replace(/[-_\s]+/g, " ")
    .trim();
  if (AIB_HUB_ALIASES.has(normalizedAlias)) {
    return "NO";
  }

  const upperValue = trimmed.toUpperCase();
  if (REGISTRY_VALUES.has(upperValue)) {
    return upperValue;
  }

  // NOTE: mirrors REGISTRY_COUNTRY_REGION_CODE_PATTERN in app/utils/eecs_utils.py.
  const candidateTokens = upperValue.match(/\b[A-Z]{2}(?:-[A-Z]{3})?\b/g) ?? [];
  const directTokenMatch = candidateTokens.find((token) =>
    REGISTRY_VALUES.has(token)
  );
  if (directTokenMatch) {
    return directTokenMatch;
  }

  const normalizedName = trimmed
    .toLowerCase()
    .replace(/\(.*?\)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (normalizedName.includes("belgium")) {
    const belgiumRegion = Object.entries(BELGIUM_REGION_TO_REGISTRY).find(
      ([regionKeyword]) => normalizedName.includes(regionKeyword)
    );
    if (belgiumRegion) {
      return belgiumRegion[1];
    }
    return "BE-FED";
  }

  const countryCode = COUNTRY_NAME_TO_CODE.get(normalizedName);
  if (!countryCode) {
    return null;
  }

  if (REGISTRY_VALUES.has(countryCode)) {
    return countryCode;
  }

  const countryPrefixedRegistries = SUPPORTED_REGISTRIES_WITH_LABELS.filter(
    (registry) => registry.value.startsWith(`${countryCode}-`)
  );
  if (countryPrefixedRegistries.length === 1) {
    return countryPrefixedRegistries[0].value;
  }

  return null;
};
